import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { Button, Text, tokens, useMqSelect } from '@fhs/ui';
import {
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from '@fhs-legacy/frontend/src/state/crm-events';

import { addInAppMessageListener } from './holiday-match-message-listener';
import { useHolidayMatchBannerConfig } from './use-holiday-match-banner-config';

// Stop loading if Braze returns no message.
const MESSAGE_WAIT_TIMEOUT_MS = 10_000;

// Keep loading after receipt while the HTML modal renders and animates in.
const MESSAGE_PRESENTATION_GRACE_MS = 2_500;

// Match the web SDK's minimum interval between in-app messages.
const MESSAGE_COOLDOWN_MS = 10_000;

export enum HolidayMatchBannerVariant {
  Banner = 'banner',
  Card = 'card',
}

type HolidayMatchBannerProps = {
  variant?: HolidayMatchBannerVariant;
};

export function HolidayMatchBanner(props: HolidayMatchBannerProps) {
  const config = useHolidayMatchBannerConfig();

  return config.enabled ? <HolidayMatchBannerContent {...props} config={config} /> : null;
}

function HolidayMatchBannerContent({
  variant = HolidayMatchBannerVariant.Banner,
  config,
}: HolidayMatchBannerProps & { config: ReturnType<typeof useHolidayMatchBannerConfig> }) {
  const isDesktop = useMqSelect({ $gteDesktop: true }, false) && Platform.OS === 'web';
  const isCard = variant === HolidayMatchBannerVariant.Card;
  const { logRBIEvent } = useCRMEventsContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const pendingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cooldownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startCooldown = useCallback(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    if (cooldownTimeout.current !== null) {
      clearTimeout(cooldownTimeout.current);
    }
    setIsOnCooldown(true);
    cooldownTimeout.current = setTimeout(() => {
      cooldownTimeout.current = null;
      setIsOnCooldown(false);
    }, MESSAGE_COOLDOWN_MS);
  }, []);

  useEffect(() => {
    const subscription = addInAppMessageListener(({ discarded }) => {
      if (discarded) {
        return;
      }

      startCooldown();
      if (pendingTimeout.current === null) {
        return;
      }

      clearTimeout(pendingTimeout.current);
      pendingTimeout.current = setTimeout(() => {
        pendingTimeout.current = null;
        setIsLoading(false);
      }, MESSAGE_PRESENTATION_GRACE_MS);
    });

    return () => {
      subscription.remove();
      if (pendingTimeout.current !== null) {
        clearTimeout(pendingTimeout.current);
      }
      if (cooldownTimeout.current !== null) {
        clearTimeout(cooldownTimeout.current);
      }
    };
  }, [startCooldown]);

  const onPressPlayNow = () => {
    if (pendingTimeout.current !== null || cooldownTimeout.current !== null) {
      return;
    }

    setIsLoading(true);
    startCooldown();
    pendingTimeout.current = setTimeout(() => {
      pendingTimeout.current = null;
      setIsLoading(false);
    }, MESSAGE_WAIT_TIMEOUT_MS);

    logRBIEvent({
      name: CustomEventNames.APP_MODULE_CTA_CLICK,
      type: EventTypes.UserContent,
      attributes: {
        campaign_id: config.campaignId,
      },
    });
  };

  return (
    <View
      style={[
        styles.container,
        isDesktop && styles.desktopContainer,
        isCard && styles.cardContainer,
        isCard && isDesktop && styles.desktopCardContainer,
      ]}
      testID="holiday-match-banner"
    >
      <Text
        style={[
          styles.message,
          isDesktop && styles.desktopMessage,
          isCard && styles.cardMessage,
          isCard && !isDesktop && styles.mobileCardMessage,
        ]}
        numberOfLines={isDesktop && !isCard ? 1 : undefined}
      >
        <Text weight="bold" style={styles.title}>
          {config.title}:{' '}
        </Text>
        {config.description}
      </Text>
      <Button
        type="solid"
        size={isCard && isDesktop ? 'sm' : 'xs'}
        style={[styles.button, isCard && isDesktop && styles.desktopCardButton]}
        focusedStyle={Platform.OS === 'web' ? styles.webButtonFocus : undefined}
        loading={isLoading}
        disabled={isOnCooldown && !isLoading}
        disabledStyle={styles.cooldownButton}
        onPress={onPressPlayNow}
        accessibilityLabel={isLoading ? 'Loading holiday game' : config.buttonText}
        accessibilityHint={
          isOnCooldown && !isLoading ? 'Please wait before playing again.' : undefined
        }
        accessibilityState={{ busy: isLoading, disabled: isLoading || isOnCooldown }}
      >
        {isLoading ? null : (
          <Button.Text style={[styles.buttonText, isDesktop && styles.desktopButtonText]}>
            {config.buttonText}
          </Button.Text>
        )}
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#17402F',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  message: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    color: tokens.colors.$white,
    fontSize: 12,
    lineHeight: 16.8,
  },
  title: {
    color: tokens.colors.$white,
  },
  button: {
    minWidth: 81,
  },
  webButtonFocus: {
    backgroundColor: tokens.colors.$houseRed,
    borderColor: tokens.colors.$houseRed,
  },
  cooldownButton: {
    backgroundColor: tokens.colors.$houseRed,
    borderColor: tokens.colors.$houseRed,
    opacity: 0.6,
  },
  buttonText: {
    color: tokens.colors.$white,
    fontSize: 10,
    lineHeight: 14,
    paddingHorizontal: 0,
  },
  desktopContainer: {
    paddingVertical: 16,
    gap: 24,
    justifyContent: 'center',
  },
  desktopMessage: {
    flexGrow: 0,
    fontSize: 16,
    lineHeight: 22.4,
  },
  desktopButtonText: {
    fontSize: 12,
    lineHeight: 16.8,
  },
  cardContainer: {
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    borderRadius: 8,
    gap: 12,
  },
  cardMessage: {
    flexGrow: 1,
  },
  mobileCardMessage: {
    fontSize: 14,
    lineHeight: 19.6,
  },
  desktopCardContainer: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    gap: 24,
    minHeight: 64,
  },
  desktopCardButton: {
    width: 128,
  },
});
